import type { ValidationConfig, ValidationIssue } from '../types/model';
import type { JsonSchema } from '../types/schema';
import { PRIMITIVES, collectFieldNamesInChain, getArrayValues, isInteger, isNumber, isPlainObject, normalizeName } from './modelUtils';
import { schemaToEnums, schemaToTypes } from './schemaUtils';

export const validateSchema = (
  schema: JsonSchema,
  config: ValidationConfig,
  clearedDefaultFields: Record<string, boolean>,
  jsonDefaultErrors: Record<string, boolean>
): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];
  const types = schemaToTypes(schema);
  const enums = schemaToEnums(schema);
  const typesById = new Map(types.map((type) => [type.id, type]));
  const enumsById = new Map(enums.map((entry) => [entry.id, entry]));

  const addIssue = (message: string, location?: ValidationIssue['location'], level: 'error' | 'warning' = 'error') => {
    const locationKey = [location?.typeId, location?.fieldId, location?.enumId].filter(Boolean).join('|');
    const id = `${level}-${message}-${locationKey}`;
    issues.push({ id, message, level, location });
  };

  const typeNameMap = new Map<string, string[]>();
  types.forEach((type) => {
    const key = normalizeName(type.name);
    if (!key) {
      addIssue('Type name is required.', { typeId: type.id });
      return;
    }
    const list = typeNameMap.get(key) ?? [];
    list.push(type.id);
    typeNameMap.set(key, list);
  });
  typeNameMap.forEach((ids) => {
    if (ids.length > 1) {
      ids.forEach((id) => {
        const typeName = typesById.get(id)?.name || 'Unnamed type';
        addIssue(`Type name "${typeName}" is duplicated.`, { typeId: id });
      });
    }
  });

  const enumNameMap = new Map<string, string[]>();
  enums.forEach((entry) => {
    const key = normalizeName(entry.name);
    if (!key) {
      addIssue('Enum name is required.', { enumId: entry.id });
      return;
    }
    const list = enumNameMap.get(key) ?? [];
    list.push(entry.id);
    enumNameMap.set(key, list);
  });
  enumNameMap.forEach((ids) => {
    if (ids.length > 1) {
      ids.forEach((id) => {
        const enumName = enumsById.get(id)?.name || 'Unnamed enum';
        addIssue(`Enum name "${enumName}" is duplicated.`, { enumId: id });
      });
    }
  });

  enums.forEach((entry) => {
    if (entry.values.length === 0) {
      addIssue(`Enum "${entry.name || 'Unnamed enum'}" must have at least one value.`, { enumId: entry.id });
    }
    entry.values.forEach((value) => {
      if (!value.label.trim()) {
        addIssue(`Enum "${entry.name || 'Unnamed enum'}" has a value with no label.`, { enumId: entry.id });
      }
    });
  });

  types.forEach((type) => {
    if (type.parentTypeId && !typesById.has(type.parentTypeId)) {
      addIssue(`Type "${type.name || 'Unnamed type'}" has a missing parent type.`, { typeId: type.id });
    }
    if (type.parentTypeId === type.id) {
      addIssue(`Type "${type.name || 'Unnamed type'}" cannot inherit from itself.`, { typeId: type.id });
    }
    if (config.enforceIndexedInheritance && type.isIndexed && type.parentTypeId) {
      const parent = typesById.get(type.parentTypeId);
      if (parent && !parent.isIndexed) {
        addIssue(
          `Indexed type "${type.name || 'Unnamed type'}" cannot inherit from a non-indexed parent when enforcement is on.`,
          { typeId: type.id }
        );
      }
    }
  });

  const visitState = new Map<string, 'visiting' | 'done'>();
  const detectCycles = (typeId: string) => {
    const state = visitState.get(typeId);
    if (state === 'visiting') {
      const typeName = typesById.get(typeId)?.name || 'Unnamed type';
      addIssue(`Inheritance for "${typeName}" creates a loop.`, { typeId });
      return;
    }
    if (state === 'done') {
      return;
    }
    visitState.set(typeId, 'visiting');
    const type = typesById.get(typeId);
    if (type?.parentTypeId) {
      detectCycles(type.parentTypeId);
    }
    visitState.set(typeId, 'done');
  };
  types.forEach((type) => detectCycles(type.id));

  types.forEach((type) => {
    const typeName = type.name || 'Unnamed type';
    const ancestorNames = type.parentTypeId ? collectFieldNamesInChain(typesById, type.parentTypeId) : new Set<string>();
    const localNames = new Set<string>();

    type.fields.forEach((field) => {
      const fieldKey = normalizeName(field.name);
      const location = { typeId: type.id, fieldId: field.id };

      if (!fieldKey) {
        addIssue(`Field name is required in type "${typeName}".`, location);
        return;
      }

      const fieldLabel = field.name;
      if (localNames.has(fieldKey)) {
        addIssue(`Field "${fieldLabel}" is duplicated within type "${typeName}".`, location);
      }
      if (ancestorNames.has(fieldKey)) {
        addIssue(`Field "${fieldLabel}" already exists in an ancestor of "${typeName}".`, location);
      }
      localNames.add(fieldKey);

      const allowDefaultValidation = !field.isArray;

      if (field.type.kind === 'enum') {
        const enumEntry = enumsById.get(field.type.enumId);
        if (!enumEntry) {
          addIssue(`Field "${fieldLabel}" in "${typeName}" references a missing enum.`, location);
        } else if (allowDefaultValidation) {
          const valueIds = new Set(enumEntry.values.map((value) => value.id));
          if (field.defaultValue !== null && field.defaultValue !== undefined) {
            if (field.isArray) {
              if (!Array.isArray(field.defaultValue)) {
                addIssue(`Default value for "${fieldLabel}" must be a list.`, location);
              } else {
                field.defaultValue.forEach((value) => {
                  if (typeof value !== 'string' || !valueIds.has(value)) {
                    addIssue(`Default value for "${fieldLabel}" must use valid enum entries.`, location);
                  }
                });
              }
            } else {
              if (typeof field.defaultValue !== 'string' || !valueIds.has(field.defaultValue)) {
                addIssue(`Default value for "${fieldLabel}" must be one of the enum values.`, location);
              }
            }
          }
        }
      }

      if (field.type.kind === 'type') {
        const referenced = typesById.get(field.type.typeId);
        if (!referenced) {
          addIssue(`Field "${fieldLabel}" in "${typeName}" references a missing type.`, location);
        }
      }

      if (field.type.kind === 'reference') {
        const referenced = typesById.get(field.type.typeId);
        if (!referenced) {
          addIssue(`Field "${fieldLabel}" in "${typeName}" references a missing type.`, location);
        }
      }

      if (field.type.kind === 'primitive') {
        if (!PRIMITIVES.includes(field.type.primitive)) {
          addIssue(`Field "${fieldLabel}" in "${typeName}" uses an unsupported primitive type.`, location);
        }
      }

      if (allowDefaultValidation && field.isArray && field.defaultValue !== null && field.defaultValue !== undefined) {
        if (!Array.isArray(field.defaultValue)) {
          addIssue(`Default value for "${fieldLabel}" must be a list.`, location);
        } else if (field.defaultValue.some((item) => Array.isArray(item))) {
          addIssue(`Arrays cannot contain arrays in "${fieldLabel}".`, location);
        }
      }

      if (!field.isArray && field.defaultValue !== null && field.defaultValue !== undefined) {
        if (field.type.kind === 'primitive') {
          if (field.type.primitive === 'string' && typeof field.defaultValue !== 'string') {
            addIssue(`Default value for "${fieldLabel}" must be text.`, location);
          }
          if (field.type.primitive === 'int' && !isInteger(field.defaultValue)) {
            addIssue(`Default value for "${fieldLabel}" must be an integer.`, location);
          }
          if (field.type.primitive === 'float' && !isNumber(field.defaultValue)) {
            addIssue(`Default value for "${fieldLabel}" must be a number.`, location);
          }
          if (field.type.primitive === 'boolean' && typeof field.defaultValue !== 'boolean') {
            addIssue(`Default value for "${fieldLabel}" must be true or false.`, location);
          }
        }
        if (field.type.kind === 'type' && !isPlainObject(field.defaultValue)) {
          addIssue(`Default value for "${fieldLabel}" must be a JSON object.`, location);
        }
        if (field.type.kind === 'reference' && typeof field.defaultValue !== 'string') {
          addIssue(`Default value for "${fieldLabel}" must be text.`, location);
        }
      }

      if (
        allowDefaultValidation &&
        field.isArray &&
        field.defaultValue !== null &&
        field.defaultValue !== undefined &&
        Array.isArray(field.defaultValue)
      ) {
        const defaultArray = getArrayValues(field.defaultValue);
        if (field.type.kind === 'primitive') {
          const primitive = field.type.primitive;
          defaultArray.forEach((value) => {
            if (primitive === 'string' && typeof value !== 'string') {
              addIssue(`Default list for "${fieldLabel}" must contain only text values.`, location);
            }
            if (primitive === 'int' && !isInteger(value)) {
              addIssue(`Default list for "${fieldLabel}" must contain only integers.`, location);
            }
            if (primitive === 'float' && !isNumber(value)) {
              addIssue(`Default list for "${fieldLabel}" must contain only numbers.`, location);
            }
            if (primitive === 'boolean' && typeof value !== 'boolean') {
              addIssue(`Default list for "${fieldLabel}" must contain only true/false values.`, location);
            }
          });
        }
        if (field.type.kind === 'type') {
          defaultArray.forEach((value) => {
            if (!isPlainObject(value)) {
              addIssue(`Default list for "${fieldLabel}" must contain only objects.`, location);
            }
          });
        }
      }

      const { minLength, maxLength, minValue, maxValue, minItems, maxItems } = field.constraints;

      if (clearedDefaultFields[field.id] && allowDefaultValidation) {
        addIssue(`Default for "${fieldLabel}" was cleared. Pick a new default value.`, location, 'warning');
      }

      if (jsonDefaultErrors[field.id] && allowDefaultValidation) {
        addIssue(`Default value for "${fieldLabel}" must be valid JSON.`, location);
      }
      if (
        (field.type.kind === 'primitive' && field.type.primitive === 'string') ||
        field.type.kind === 'reference'
      ) {
        if (minLength !== undefined && minLength < 0) {
          addIssue(`Minimum length for "${fieldLabel}" cannot be negative.`, location);
        }
        if (maxLength !== undefined && maxLength < 0) {
          addIssue(`Maximum length for "${fieldLabel}" cannot be negative.`, location);
        }
        if (minLength !== undefined && maxLength !== undefined && minLength > maxLength) {
          addIssue(`Minimum length for "${fieldLabel}" cannot exceed the maximum length.`, location);
        }
        if (typeof field.defaultValue === 'string') {
          if (minLength !== undefined && field.defaultValue.length < minLength) {
            addIssue(`Default for "${fieldLabel}" is shorter than the minimum length.`, location);
          }
          if (maxLength !== undefined && field.defaultValue.length > maxLength) {
            addIssue(`Default for "${fieldLabel}" is longer than the maximum length.`, location);
          }
        }
      }

      if (field.type.kind === 'primitive' && (field.type.primitive === 'int' || field.type.primitive === 'float')) {
        if (minValue !== undefined && maxValue !== undefined && minValue > maxValue) {
          addIssue(`Minimum value for "${fieldLabel}" cannot exceed the maximum value.`, location);
        }
        if (field.type.primitive === 'int') {
          if (minValue !== undefined && !Number.isInteger(minValue)) {
            addIssue(`Minimum value for "${fieldLabel}" must be an integer.`, location);
          }
          if (maxValue !== undefined && !Number.isInteger(maxValue)) {
            addIssue(`Maximum value for "${fieldLabel}" must be an integer.`, location);
          }
        }
        if (isNumber(field.defaultValue)) {
          if (minValue !== undefined && field.defaultValue < minValue) {
            addIssue(`Default for "${fieldLabel}" is below the minimum value.`, location);
          }
          if (maxValue !== undefined && field.defaultValue > maxValue) {
            addIssue(`Default for "${fieldLabel}" is above the maximum value.`, location);
          }
        }
      }

      if (field.isArray) {
        if (minItems !== undefined && minItems < 0) {
          addIssue(`Minimum items for "${fieldLabel}" cannot be negative.`, location);
        }
        if (maxItems !== undefined && maxItems < 0) {
          addIssue(`Maximum items for "${fieldLabel}" cannot be negative.`, location);
        }
        if (minItems !== undefined && maxItems !== undefined && minItems > maxItems) {
          addIssue(`Minimum items for "${fieldLabel}" cannot exceed the maximum items.`, location);
        }
        if (Array.isArray(field.defaultValue)) {
          if (minItems !== undefined && field.defaultValue.length < minItems) {
            addIssue(`Default list for "${fieldLabel}" has fewer items than allowed.`, location);
          }
          if (maxItems !== undefined && field.defaultValue.length > maxItems) {
            addIssue(`Default list for "${fieldLabel}" has more items than allowed.`, location);
          }
        }
      }
    });
  });

  return issues;
};
